import { useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactCard from '../components/ContactCard';

const ppcHighlights = [
  {
    title: 'Cut CPL by 48%',
    detail: 'Scaled Google Search + Performance Max for a home services brand while reducing cost per lead nearly in half.',
  },
  {
    title: '3.8x ROAS in 60 days',
    detail: 'Meta ads + retargeting sequences for a DTC retailer generated record-breaking Q4 revenue.',
  },
  {
    title: 'Pipeline-first reporting',
    detail: 'Live dashboards connect ad spend to demo bookings, sign-ups, and revenue — no more guesswork.',
  },
];

const ppcDeliverables = [
  {
    heading: 'Full-funnel ad strategy',
    text: 'Audience research, offer positioning, and media plans that map each campaign to your buyer journey.',
  },
  {
    heading: 'Creative + landing optimisation',
    text: 'On-brand visuals, thumb-stopping copy, and conversion-tested landing pages built for speed.',
  },
  {
    heading: 'Always-on experimentation',
    text: 'Structured testing roadmap that iterates bids, targeting, and messaging every single week.',
  },
];

const ppcProcess = [
  {
    title: 'Week 1: Deep audit & tracking',
    copy: 'We analyse historical performance, rebuild conversion tracking, and deliver a quick-win action list.',
  },
  {
    title: 'Weeks 2-3: Launch & validate',
    copy: 'Activate refreshed campaigns, deploy new creative, and gather statistically significant learnings fast.',
  },
  {
    title: 'Week 4 onwards: Scale & optimise',
    copy: 'Ramp budgets into winning audiences, test new offers, and expand channels without wasting spend.',
  },
];

export default function PPCLanding() {
  const contactRef = useRef(null);
  const handleTalkClick = () => {
    contactRef.current?.focusForm?.();
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-white via-sky-50 to-indigo-100 font-sans text-slate-900">
      <Navbar onTalkClick={handleTalkClick} />

      <main className="flex-1 pt-32">
        <section id="services" className="mx-auto max-w-6xl px-6">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Performance marketing, done right</p>
              <h1 className="mt-4 text-4xl font-semibold md:text-5xl">Paid campaigns that turn ad spend into revenue</h1>
              <p className="mt-4 text-lg text-slate-600">
                Stop boosting posts and guessing at keywords. We design, launch, and optimise high-performing Google,
                Meta, and multi-channel campaigns that deliver predictable results.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleTalkClick}
                  className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow hover:opacity-90"
                >
                  Request a PPC audit →
                </button>
                <a
                  href="tel:14374944736"
                  className="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 transition hover:bg-white"
                >
                  Talk now: 1‑437‑494‑4736
                </a>
              </div>
            </div>
            <div className="grid gap-4">
              {ppcHighlights.map((item) => (
                <div key={item.title} className="rounded-3xl border border-slate-200 bg-white/70 p-5 shadow-sm backdrop-blur">
                  <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-6xl px-6">
          <div className="grid gap-6 md:grid-cols-3">
            {ppcDeliverables.map((deliverable) => (
              <article
                key={deliverable.heading}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <h3 className="text-xl font-semibold text-slate-900">{deliverable.heading}</h3>
                <p className="mt-3 text-sm text-slate-600">{deliverable.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="work" className="mx-auto mt-16 max-w-6xl px-6">
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-10 shadow-sm backdrop-blur">
            <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Real results</p>
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <h2 className="text-3xl font-semibold text-slate-900">63 trials in two weeks for PSA Toronto</h2>
                <p className="mt-3 text-sm text-slate-600">
                  We rebuilt their offer positioning, created a killer landing page, and launched Google + Meta campaigns
                  targeting local and lookalike audiences. Automated follow-up turned leads into paying members fast.
                </p>
              </div>
              <div className="rounded-2xl bg-slate-900/90 p-6 text-white">
                <p className="text-sm uppercase tracking-[0.2em] text-slate-300">Snapshot</p>
                <ul className="mt-4 space-y-3 text-sm text-slate-100">
                  <li>• 4.2x return on ad spend</li>
                  <li>• 37% drop in cost per trial sign-up</li>
                  <li>• 3 automated email sequences added $28K ARR</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="process" className="mx-auto mt-16 max-w-6xl px-6">
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900">Our 3-phase sprint process</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {ppcProcess.map((phase) => (
                <div key={phase.title} className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-500">{phase.title}</h3>
                  <p className="mt-3 text-sm text-slate-600">{phase.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-4xl px-6 text-center">
          <div className="rounded-3xl border border-blue-100 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900 md:text-4xl">Ready to stop wasting ad spend?</h2>
            <p className="mt-3 text-lg text-slate-600">
              Book a free audit and we’ll show you where you can scale, where you’re leaking budget, and the experiments
              we’d run first.
            </p>
            <button
              type="button"
              onClick={handleTalkClick}
              className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-white shadow hover:opacity-90"
            >
              Get my PPC roadmap →
            </button>
          </div>
        </section>
      </main>

      <ContactCard ref={contactRef} className="mt-16" />
      <Footer />
    </div>
  );
}
